/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { getId, LayerHost } from '@fluentui/react';
import { Link } from '@fluentui/react/lib/Link';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import orderBy from 'lodash.orderby';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory, useLocation } from 'react-router';
import { CluProjectType } from 'src/clu/core/services/projects/cluProjectSwaggers';
import { DeletePromptModal } from 'src/core/components/DeletePromptModal';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { EmptyStateView } from 'src/core/components/EmptyStateView';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { SortKeyType } from 'src/core/components/TableProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useSelection } from 'src/core/hooks/useSelection';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { getScopedTheme, getStylist } from 'src/core/themes/luisStylist';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { createKeyedStringNormalizer } from 'src/core/utils/strings';
import { CustomTextProjectKind } from 'src/customText/core/stores/CustomTextProjectStore';
import { SubmitTrainingJobPanel, TrainingModelDto } from 'src/modules/modelTraining/components/SubmitTrainingJobPanel';
import { TrainingJobDetailsPanel } from 'src/modules/modelTraining/components/TrainingJobDetailsPanel';
import { TrainingJobTable } from 'src/modules/modelTraining/components/TrainingJobTable';
import { TrainingJobTableCommandBar } from 'src/modules/modelTraining/components/TrainingJobTableCommandBar';
import { TrainingJobStore } from 'src/modules/modelTraining/stores/TrainingJobInfoStore';
import { TrainingAllowanceData, TrainingConfigVersion } from 'src/modules/modelTraining/types/trainingTypes';

export type TrainingLayoutProps = {
    isLoading?: boolean;
    tagDataLink?: string;
    learnMoreLink: string;
    clickHereText?: string;
    pageDescription: string;
    trainedModelNames: string[];
    warningsSection: JSX.Element;
    isTrainingInProgress: boolean;
    isEvaluationInProgress: boolean;
    currentTrainingModelName: string;
    trainingJobs: TrainingJobStore[];
    showTrainingModeChoices?: boolean;
    advancedTrainingPricingLink?: string;
    userDefinedTestingPercentage?: number;
    userDefinedTrainingPercentage?: number;
    isUserDefinedSplittingEnabled?: boolean;
    onModelClick: (modelId: string) => void;
    trainingAllowance: TrainingAllowanceData;
    userDefinedOptionDisabledTooltip: string;
    onCancelTraining: (jobId: string) => void;
    trainingConfigVersions: TrainingConfigVersion[];
    onFireTraining: (model: TrainingModelDto) => void;
    projectType: CluProjectType | CustomTextProjectKind;
};

const messages = defineMessages({
    trainModel: { id: 'trainingLayout.trainModel', defaultMessage: 'Training jobs' },
    startTrainingJob: { id: 'trainingLayout.startTrainingJob', defaultMessage: 'Start a training job' },
    notification: { id: 'trainingLayout.notification', defaultMessage: 'Training complete notification' },
    emptyStateMessageCont: { id: 'trainingLayout.emptyStateMessageCont', defaultMessage: '” above to begin creating a model' },
    emptyStateMessage: { id: 'trainingLayout.emptyStateMessage', defaultMessage: 'Click on “' },
    clickHereText: { id: 'trainingLayout.clickHereText', defaultMessage: 'Learn about the pricing for training' },
    yes: { id: 'trainingLayout.yes', defaultMessage: 'Yes' },
    cancelTrainingModalBody: {
        id: 'trainingLayout.cancelTrainingModalBody',
        defaultMessage: 'Are you sure you want to cancel the selected training job?'
    },
    cancelTrainingModalTitle: { id: 'trainingLayout.cancelTrainingModalTitle', defaultMessage: 'Cancel training job' },
    cancelTrainingDisabledTooltip: {
        id: 'trainingLayout.cancelTrainingDisabledTooltip',
        defaultMessage: 'Cannot cancel a `Standard` training operation'
    }
});

const emptyStateTheme = getScopedTheme('EmptyState');
const { styleComponent } = getStylist('NewModelTrainingLayout');
const Container = styleComponent<IStackProps>(Stack)('Container', { overflowY: 'auto', flex: 1 });

export const TrainingLayout = observer((props: TrainingLayoutProps) => {
    const {
        projectType,
        tagDataLink,
        onModelClick,
        trainingJobs,
        clickHereText,
        learnMoreLink,
        onFireTraining,
        onCancelTraining,
        warningsSection,
        pageDescription,
        isLoading = false,
        trainedModelNames,
        trainingConfigVersions,
        isTrainingInProgress,
        isEvaluationInProgress,
        currentTrainingModelName,
        advancedTrainingPricingLink,
        showTrainingModeChoices = false,
        userDefinedOptionDisabledTooltip,
        userDefinedTestingPercentage = 0,
        userDefinedTrainingPercentage = 0,
        trainingAllowance: isTrainingAllowed,
        isUserDefinedSplittingEnabled = false
    } = props;

    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const location = useLocation();
    const history = useHistory();

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);
    const [isCancelTrainingJobModalHidden, setIsCancelTrainingJobModalHidden] = React.useState(true);

    const [sortKey, setSortKey] = React.useState<SortKeyType<TrainingJobStore>>({ key: 'startDateTime', order: 'desc' });

    const [query, setQuery] = React.useState('');
    const [startTrainingJobPanelSettings, updateStartTrainingJobPanelSettings] = useUpdateState<{
        isOpen: boolean;
    }>({ isOpen: false });

    const decodedSelectedJobId = (location.state as { selectedJobId: string })?.selectedJobId ?? '';

    const [trainingJobDetailsPanelSettings, updateTrainingJobDetailsPanelSettings] = useUpdateState<{
        isOpen: boolean;
        selectedJobId: string;
    }>({ isOpen: false, selectedJobId: decodedSelectedJobId });

    const { selectedItems, selection } = useSelection<TrainingJobStore>();

    const isTrainingDisabled = !isTrainingAllowed.isAllowed || isTrainingInProgress || isEvaluationInProgress;
    const isInEmptyState = trainingJobs.length === 0 && !isLoading;

    const trainingMode = React.useMemo(() => selectedItems[0]?.trainingMode, [selectedItems]);

    const isCancelTrainingJobDisabled = React.useMemo(
        () =>
            !selectedItems.length ||
            selectedItems[0]?.status !== 'inProgress' ||
            (projectType === 'Conversation' && trainingMode === 'standard'),
        [selectedItems, projectType, trainingMode]
    );

    const cancelTrainingJobDisabledMsg =
        selectedItems[0]?.status === 'inProgress' && projectType === 'Conversation' && trainingMode === 'standard'
            ? localize(messages.cancelTrainingDisabledTooltip)
            : null;

    React.useEffect(() => {
        if (
            decodedSelectedJobId &&
            (decodedSelectedJobId !== trainingJobDetailsPanelSettings.selectedJobId || !trainingJobDetailsPanelSettings.isOpen)
        ) {
            updateTrainingJobDetailsPanelSettings({ isOpen: true, selectedJobId: decodedSelectedJobId });
            history.replace({ ...history.location, state: undefined });
        }
    }, [decodedSelectedJobId]);

    const filteredJobs = orderBy(
        substringSearchStrategy(trainingJobs, query, ['id', 'modelId']),
        [createKeyedStringNormalizer(sortKey.key)],
        [sortKey.order]
    );

    const fireTraining = (model: TrainingModelDto) => {
        updateStartTrainingJobPanelSettings({ isOpen: false });
        onFireTraining(model);
        selection.setAllSelected(false);
    };

    const _layerHostId = getId('layerHost');

    const handleCancelTrainingJob = () => {
        setIsCancelTrainingJobModalHidden(true);
        onCancelTraining(selectedItems[0]?.id);
        selection.setAllSelected(false);
    };

    const subtitle = (
        <>
            {pageDescription}
            &nbsp;
            <ExternalLink href="https://aka.ms/language-pricing">{clickHereText ?? localize(messages.clickHereText)}</ExternalLink>
        </>
    );

    return (
        <>
            <Container tokens={{ childrenGap: 12 }}>
                <LayerHost id={_layerHostId}>
                    <Stack.Item>
                        <EditableTitle title={localize(messages.trainModel)} subtitle={subtitle} />
                    </Stack.Item>

                    {warningsSection}

                    <TrainingJobTableCommandBar
                        query={query}
                        onQueryChange={setQuery}
                        isStartTrainingJobDisabled={isTrainingDisabled}
                        startTrainingJobDisabledMsg={isTrainingAllowed.message}
                        isCancelTrainingJobDisabled={isCancelTrainingJobDisabled}
                        cancelTrainingJobDisabledMsg={cancelTrainingJobDisabledMsg}
                        onCancelTrainingJob={() => setIsCancelTrainingJobModalHidden(false)}
                        onStartTrainingJob={() => updateStartTrainingJobPanelSettings({ isOpen: true })}
                    />

                    {isInEmptyState ? (
                        <EmptyStateView
                            message={
                                <LuisText>
                                    {localize(messages.emptyStateMessage)}&nbsp;
                                    <RBACTooltip
                                        displayedUserRole={displayedUserRole}
                                        hidden={canWrite || isTrainingDisabled}
                                        data-automation-id="startTrainingJobLinkLanguageToolTip"
                                        componentSurroundedByToolTip={
                                            <Link
                                                disabled={!canWrite || isTrainingDisabled}
                                                styles={{ root: { color: emptyStateTheme.emptyStateLinkColor } }}
                                                onClick={() => updateStartTrainingJobPanelSettings({ isOpen: true })}>
                                                {localize(messages.startTrainingJob)}
                                            </Link>
                                        }
                                    />
                                    {localize(messages.emptyStateMessageCont)}
                                </LuisText>
                            }
                        />
                    ) : (
                        <TrainingJobTable
                            sortKey={sortKey}
                            items={filteredJobs}
                            selection={selection}
                            isLoading={isLoading}
                            onModelClick={onModelClick}
                            areItemsFiltered={Boolean(query)}
                            trainedModelNames={trainedModelNames}
                            overallItemsCount={filteredJobs.length}
                            onTrainingJobClick={selectedJobId => updateTrainingJobDetailsPanelSettings({ isOpen: true, selectedJobId })}
                            onSortColumn={c =>
                                setSortKey({ key: c.fieldName as keyof TrainingJobStore, order: c.isSortedDescending ? 'asc' : 'desc' })
                            }
                        />
                    )}
                </LayerHost>
            </Container>

            <SubmitTrainingJobPanel
                tagDataLink={tagDataLink}
                onSubmitJob={fireTraining}
                learnMoreLink={learnMoreLink}
                layerProps={{ hostId: _layerHostId }}
                trainedModelNames={trainedModelNames}
                isOpen={startTrainingJobPanelSettings.isOpen}
                trainingConfigVersions={trainingConfigVersions}
                showTrainingModeChoices={showTrainingModeChoices}
                currentTrainingModelName={currentTrainingModelName}
                advancedTrainingPricingLink={advancedTrainingPricingLink}
                userDefinedTestingPercentage={userDefinedTestingPercentage}
                userDefinedTrainingPercentage={userDefinedTrainingPercentage}
                isUserDefinedSplittingEnabled={isUserDefinedSplittingEnabled}
                userDefinedOptionDisabledTooltip={userDefinedOptionDisabledTooltip}
                onDismiss={() => updateStartTrainingJobPanelSettings({ isOpen: false })}
            />

            <TrainingJobDetailsPanel
                onModelClick={onModelClick}
                isOpen={trainingJobDetailsPanelSettings.isOpen}
                onDismiss={() => updateTrainingJobDetailsPanelSettings({ isOpen: false })}
                trainingJob={trainingJobs.find(tj => tj.id === trainingJobDetailsPanelSettings.selectedJobId)}
            />

            <DeletePromptModal
                onDelete={handleCancelTrainingJob}
                hidden={isCancelTrainingJobModalHidden}
                submitButtonText={localize(messages.yes)}
                onDismiss={() => setIsCancelTrainingJobModalHidden(true)}
                dialogContentProps={{ title: localize(messages.cancelTrainingModalTitle) }}>
                {localize(messages.cancelTrainingModalBody)}
            </DeletePromptModal>
        </>
    );
});
